import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, CheckCircle2, Circle, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loader, ScriptureQuote, ScriptureRef } from "@/components/cast/Common";
import { refreshStreak } from "@/components/cast/Streak";

function Lesson({ mid, lesson, idx, done, onToggle }: any) {
  const [qs, setQs] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const gen = async () => {
    setBusy(true);
    try { setQs((await api.post("/ai/study-questions", { ref: lesson.ref, text: lesson.text })).data.questions); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    try { await api.post("/notes", { ref: lesson.ref, content: note, source: `Study: ${lesson.title}` }); setNote(""); toast.success("Note saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="card-warm space-y-6 p-6 sm:p-10" data-testid={`lesson-${idx}`}>
      <div className="flex items-start justify-between gap-4">
        <div><p className="eyebrow">Lesson {idx + 1}</p><h2 className="mt-2 font-display text-3xl font-semibold text-slate-900">{lesson.title}</h2></div>
        <button data-testid={`lesson-toggle-${idx}`} onClick={onToggle} className="flex items-center gap-1.5 rounded-full border border-[#E2DBCE] px-3 py-1.5 text-sm font-semibold transition-colors hover:bg-[#F3ECE0]">
          {done ? <CheckCircle2 className="h-4 w-4 text-[#5B7065]" /> : <Circle className="h-4 w-4" />}{done ? "Completed" : "Mark complete"}
        </button>
      </div>
      <ScriptureQuote text={lesson.text} refText={lesson.ref} save={{ title: `Study: ${lesson.title}`, link: `/deep-water/${mid}` }} />
      <div><p className="text-sm font-semibold text-slate-800">Reflect</p><ul className="mt-2 space-y-1.5">{lesson.prompts.map((p: string) => <li key={p} className="text-slate-700">— {p}</li>)}</ul></div>
      <div className="rounded-2xl bg-[#F3ECE0]/60 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-2 font-semibold text-slate-800"><Sparkles className="h-4 w-4 text-[#C86D51]" />AI study questions</p>
          <Button size="sm" data-testid={`lesson-generate-questions-${idx}`} onClick={gen} disabled={busy} className="rounded-full bg-[#1E293B]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : qs ? "Regenerate" : "Generate"}</Button>
        </div>
        {qs && (
          <ol className="mt-4 list-decimal space-y-3 pl-5" data-testid={`lesson-questions-${idx}`}>
            {qs.map((q, i) => <li key={i} className="text-slate-800">{q.question} <span className="ml-1 inline-flex flex-wrap gap-1 align-middle">{q.refs.map((r: string) => <ScriptureRef key={r} refText={r} />)}</span></li>)}
          </ol>
        )}
        <p className="mt-3 text-xs text-slate-500">Generated questions are study aids, anchored to the cited verses.</p>
      </div>
      <div className="space-y-2">
        <Textarea data-testid={`lesson-note-input-${idx}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder={`Your notes on ${lesson.ref}…`} className="bg-white" />
        <Button size="sm" data-testid={`lesson-note-save-${idx}`} disabled={!note.trim()} onClick={save} className="rounded-full bg-[#C86D51] hover:bg-[#B35A3F]">Save note</Button>
      </div>
    </section>
  );
}

export default function Module() {
  const { moduleId } = useParams();
  const [m, setM] = useState<any>(null);
  useEffect(() => { api.get(`/modules/${moduleId}`).then((r) => setM(r.data)); }, [moduleId]);
  if (!m) return <Loader />;
  const toggle = async (i: number) => { const { data } = await api.post(`/modules/${m.id}/lessons/${i}/toggle`); setM({ ...m, completed: data.completed }); refreshStreak(); };
  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <Link to="/deep-water" data-testid="module-back-link" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600"><ChevronLeft className="h-4 w-4" />Deep Water</Link>
      <div>
        <p className="eyebrow">Deep Water · {m.category} study</p>
        <h1 className="mt-3 font-display text-4xl font-semibold text-slate-900 sm:text-5xl" data-testid="module-title">{m.title}</h1>
        <p className="mt-3 text-lg text-slate-600">{m.summary}</p>
        <div className="mt-4 flex flex-wrap gap-2">{m.lessons.map((l: any) => <ScriptureRef key={l.ref} refText={l.ref} />)}</div>
      </div>
      {m.lessons.map((l: any, i: number) => <Lesson key={i} mid={m.id} lesson={l} idx={i} done={m.completed.includes(i)} onToggle={() => toggle(i)} />)}
    </div>
  );
}
